import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import {
  useCreatePickupCity,
  useDeletePickupCity,
  usePickupCities,
  useUpdatePickupCity,
} from '../../hooks/usePickupCities';
import type { PickupCity } from '../../types/pickupCity';
import { PICKUP_ZONE_CITIES_FIXTURE } from '../pickup/public/data/pickupZones.fixtures';

const { Title, Paragraph, Text } = Typography;

type FormValues = {
  label: string;
  districtsText: string;
  sortOrder: number;
  active: boolean;
};

function parseDistricts(text: string): string[] {
  return String(text || '')
    .split(/[,;\n]/)
    .map((d) => d.trim())
    .filter(Boolean);
}

/**
 * Super Admin — catalogue villes / quartiers (source de vérité pour ramassage).
 */
const PickupCitiesAdmin: React.FC = () => {
  const { data: cities = [], isLoading } = usePickupCities({ activeOnly: false });
  const createCity = useCreatePickupCity();
  const updateCity = useUpdatePickupCity();
  const deleteCity = useDeletePickupCity();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PickupCity | null>(null);
  const [form] = Form.useForm<FormValues>();

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      label: '',
      districtsText: '',
      sortOrder: (cities.length + 1) * 10,
      active: true,
    });
    setOpen(true);
  };

  const openEdit = (city: PickupCity) => {
    setEditing(city);
    form.setFieldsValue({
      label: city.label,
      districtsText: city.districts.join(', '),
      sortOrder: city.sortOrder,
      active: city.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const districts = parseDistricts(values.districtsText);
    if (editing) {
      await updateCity.mutateAsync({
        id: editing.id,
        label: values.label,
        districts,
        sortOrder: values.sortOrder,
        active: values.active,
      });
    } else {
      await createCity.mutateAsync({
        label: values.label,
        districts,
        sortOrder: values.sortOrder,
        active: values.active,
      });
    }
    setOpen(false);
  };

  const seedBase = async () => {
    const existing = new Set(cities.map((c) => c.label.toLowerCase()));
    let order = cities.length * 10;
    for (const fixture of PICKUP_ZONE_CITIES_FIXTURE) {
      if (existing.has(fixture.label.toLowerCase())) continue;
      order += 10;
      await createCity.mutateAsync({
        label: fixture.label,
        districts: fixture.districts,
        sortOrder: order,
        active: true,
        country: 'CM',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Ville',
        dataIndex: 'label',
        key: 'label',
        render: (label: string, row: PickupCity) => (
          <Space>
            <Text strong>{label}</Text>
            {!row.active && <Tag>Inactive</Tag>}
          </Space>
        ),
      },
      {
        title: 'Quartiers',
        dataIndex: 'districts',
        key: 'districts',
        render: (districts: string[]) => (
          <Text type="secondary">{districts.length} zone(s)</Text>
        ),
      },
      {
        title: 'Ordre',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_: unknown, row: PickupCity) => (
          <Space>
            <Button type="link" onClick={() => openEdit(row)}>
              Modifier
            </Button>
            <Popconfirm
              title="Supprimer cette ville ?"
              onConfirm={() => deleteCity.mutate(row.id)}
            >
              <Button type="link" danger>
                Supprimer
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteCity]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <EnvironmentOutlined /> Villes &amp; quartiers
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Catalogue NauvaTek utilisé sur /pressing/ramassage et dans la config zones des
            pressings. Le matching marketplace se fait d&apos;abord par ville.
          </Paragraph>
        </div>
        <Space>
          <Button onClick={seedBase} loading={createCity.isPending}>
            Charger Douala / Yaoundé / Bafoussam
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Ajouter une ville
          </Button>
        </Space>
      </Space>

      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={cities}
          columns={columns}
          expandable={{
            expandedRowRender: (row) => (
              <div style={{ padding: '4px 0' }}>
                {row.districts.length ? (
                  <Space wrap size={[4, 8]}>
                    {row.districts.map((d) => (
                      <Tag key={d}>{d}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">Aucun quartier</Text>
                )}
              </div>
            ),
          }}
          pagination={false}
        />
      </Card>

      <Modal
        title={editing ? 'Modifier la ville' : 'Nouvelle ville'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createCity.isPending || updateCity.isPending}
        destroyOnClose
        okText="Enregistrer"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label="Nom de la ville"
            rules={[{ required: true, message: 'Ville requise' }]}
          >
            <Input placeholder="Ex: Douala" />
          </Form.Item>
          <Form.Item
            name="districtsText"
            label="Quartiers"
            extra="Séparés par virgule ou retour à la ligne"
            rules={[{ required: true, message: 'Au moins un quartier' }]}
          >
            <Input.TextArea rows={5} placeholder="Akwa, Bonanjo, Deido…" />
          </Form.Item>
          <Form.Item name="sortOrder" label="Ordre d’affichage" initialValue={10}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PickupCitiesAdmin;
